import { DateTime } from 'luxon'

import { ArgentoInsurancePaymentPeriodicities, SubBranches } from '~/config'
import { DocumentTypeEnum, GenderEnum, type Identification } from '~/layers/forms/models'

import { Insured } from './insured'
import {
  Address,
  AditionalInfo,
  AlternativeVehicle,
  Beneficiary,
  Biometrics,
  Collection,
  Contact,
  Coverage,
  Currency,
  Customer,
  Device,
  PaymentInfo,
  PEP,
  Person,
  Pet,
  RealState,
  SpecialConditions,
  Travel,
  type Traveler,
  UTM,
  Vehicle,
} from '.'

import type { User } from '#auth-utils'

export type Contract = {
  address: Address
  aditionalInfo: AditionalInfo
  alternativeVehicle: AlternativeVehicle
  beneficiaries: Collection<Beneficiary>
  biometrics: Biometrics
  contact: Contact
  coverage: Coverage
  customer: Customer
  device: Device
  insured: Insured
  insuredAmount: Currency
  paymentInfo: PaymentInfo
  pep: PEP
  pet: Pet
  realState: RealState
  specialConditions: SpecialConditions
  tos: boolean
  travel: Travel
  travelers: Collection<Traveler>
  utm: UTM
  vehicle: Vehicle
}

function load(model?: Partial<Contract> | null, user?: User | null): Contract {
  return {
    address: model?.address ?? Address.load(),
    aditionalInfo: model?.aditionalInfo ?? AditionalInfo.load(),
    alternativeVehicle: model?.alternativeVehicle ?? AlternativeVehicle.load(),
    beneficiaries: model?.beneficiaries ?? Collection.load(),
    biometrics: model?.biometrics ?? Biometrics.load(null, user),
    coverage: model?.coverage ?? Coverage.load(),
    customer: model?.customer ?? Customer.load(null, user),
    insured: model?.insured ?? Insured.load(),
    device: model?.device ?? Device.load(),
    insuredAmount: model?.insuredAmount ?? Currency.load(),
    paymentInfo: model?.paymentInfo ?? PaymentInfo.load(),
    contact: model?.contact ?? Contact.load(null, user),
    pep: model?.pep ?? PEP.load(),
    pet: model?.pet ?? Pet.load(),
    realState: model?.realState ?? RealState.load(),
    specialConditions: model?.specialConditions ?? SpecialConditions.load(),
    tos: model?.tos ?? false,
    travel: model?.travel ?? Travel.load(),
    travelers: model?.travelers ?? Collection.load(),
    vehicle: model?.vehicle ?? Vehicle.load(),
    utm: model?.utm ?? UTM.load(),
  }
}

const map = {
  toProcessBuyProductRequestModel({
    userId,
    model,
    anonymous,
  }: {
    userId?: string | null
    model: Contract
    anonymous?: boolean
  }): SiteApiSchema<'ProcessBuyProductRequestModel'> {
    return {
      userId,
      anonymous,
      contract: map.toProductoContratoMasterRequestModel(model),
    }
  },
  toProductoContratoMasterRequestModel(
    model: Contract
  ): SiteApiSchema<'ProductoContratoMasterRequestModel'> {
    const { RemoteConfigurationStore } = stores()
    const requestModel: SiteApiSchema<'ProductoContratoMasterRequestModel'> = {}

    requestModel.brokerId = RemoteConfigurationStore.value?.broker?.id
    requestModel.emisorId = model.coverage.argentoInsuranceCompanyId
    requestModel.productoId = model.coverage.argentoInsuranceProductId ?? undefined
    requestModel.cliente = map.toClienteViewModel(model)

    if (RemoteConfigurationStore?.value?.form?.requiereCumplimiento) {
      requestModel.cliente.cumplimiento = PEP.map.toCumplimientoViewModel(model.pep)
    }

    requestModel.coberturaInfos = [
      {
        grandTotal: model.coverage.grandTotal,
        coberturaId: model.coverage.argentoInsuranceId,
        sumaAseguradaId: model.coverage.argentoInsuranceInsuredAmountId,
      },
    ]

    if (model.coverage?.argentoInsuranceCoverageRequestData?.extraProps) {
      model.coverage.argentoInsuranceCoverageRequestData.extraProps.forEach((extraProps) => {
        const names = model.customer?.name?.split(' ')
        extraProps.principalProps = {
          tipoIdentificacionId: model.customer?.identification?.type,
          identificacion: model.customer?.identification?.number?.replace(/\D/g, '').trim(),
          primerNombre: names?.[0] ?? '',
          segundoNombre: names?.[1] ?? '',
          primerApellido: model.customer?.firstLastName,
          segundoApellido: model.customer?.secondLastName,
          correo: model.contact?.email,
          telefono: model.contact?.phone,
        }
        const { discountAmount, discountType, discountTag } = model.paymentInfo
        if (
          discountAmount != null &&
          discountAmount > 0 &&
          discountType != null &&
          discountTag != null
        ) {
          extraProps.tagDescuento = discountTag
          extraProps.montoDescuento = discountAmount
          extraProps.tipoDescuento = discountType
        }
      })

      requestModel.coberturaInfosRQData = model.coverage.argentoInsuranceCoverageRequestData
      requestModel.coberturaInfosRQData.extraProps?.forEach(
        (ep) => (ep.condicionPagoId = model.paymentInfo.argentoInsurancePaymentConditionId)
      )
    }

    requestModel.formaPago = 'TARJETA'
    requestModel.frecuenciaPago = model.paymentInfo.argentoInsurancePaymentPeriodicity
      ? ArgentoInsurancePaymentPeriodicities[model.paymentInfo.argentoInsurancePaymentPeriodicity]
          .text
      : undefined

    if (model.paymentInfo?.argentoInsurancePaymentPeriodicity != null) {
      requestModel.frecuenciaPago =
        ArgentoInsurancePaymentPeriodicities[
          model.paymentInfo.argentoInsurancePaymentPeriodicity
        ].text.toUpperCase()
      requestModel.frecuenciaPagoId =
        ArgentoInsurancePaymentPeriodicities[
          model.paymentInfo.argentoInsurancePaymentPeriodicity
        ].id
    }

    requestModel.condicionPagoId = model.paymentInfo.argentoInsurancePaymentConditionId
    requestModel.pagoInfo = PaymentInfo.map.toPagoInfoViewModel(model.paymentInfo)

    switch (RemoteConfigurationStore?.value?.ramoEnum) {
      case 'Mascotas':
        requestModel.mascota = Pet.map.toMascotaViewModel(model.pet)
        break
      case 'Hogar':
        switch (RemoteConfigurationStore?.value?.subRamo?.nombre) {
          case SubBranches.garantiaDeElectrodomesticos:
            requestModel.asegurable = Device.map.toAsegurableViewModel(model.device)
            break
          //case SubBranches.incendioVoluntario:
          //  requestModel.inmueble = RealState.map.toInmuebleViewModel(model.realState)
          //  break
        }
        break
      case 'Auto':
        requestModel.vehiculo = Vehicle.map.toVehiculoViewModel(
          model.vehicle,
          `Plan ${model.coverage.planName}`.toUpperCase()
        )
        break
      case 'Viajes':
        requestModel.viaje = Travel.map.toViajeViewModel({
          model: model.travel,
          coverage: model.coverage,
        })
        requestModel.asegurado = map.toFillClienteByAseguradoViewModel(model)
        break
      case 'Personas':
        requestModel.asegurado = map.toFillClienteByAseguradoViewModel(model)
        requestModel.beneficiarios = Collection.map({
          data: model.beneficiaries,
          func: Beneficiary.map.toBeneficiarioViewModelModel,
        })
        break
      case 'Incendio': {
        const { asCustomerAddress } = model.realState.address
        requestModel.inmueble = {
          direccion: Address.map.toDireccionViewModel(
            asCustomerAddress ? model.address : model.realState.address
          ),
        }
        break
      }
    }

    const comentsList: string[] = []
    if (model.aditionalInfo.onBusinessBehalf) {
      comentsList.push(
        `Esta emisión debe ser realizada a nombre de la empresa ${model.aditionalInfo.business.entityName} (RNC: ${model.aditionalInfo.business.number}).`
      )
    }

    if (model.aditionalInfo.financedInsurable) {
      comentsList.push(
        `Emisión con endoso en la entidad financiera ${model.aditionalInfo.financialEntityName}.`
      )
    }

    if (comentsList.length) requestModel.comentarios = comentsList.join(' ')

    return requestModel
  },
  toClienteViewModel(
    model: Contract
  ): SiteApiSchema<'IngresosViewModelPepViewModelPepParienteViewModelPepAsociadoViewModelCumplimientoViewModelFatcaViewModelContactosViewModelDireccionViewModelDireccionViewModelEmpresaViewModelDireccionViewModelLaboralViewModelReferenciaViewModelClienteViewModel'> {
    const requestModel: SiteApiSchema<'IngresosViewModelPepViewModelPepParienteViewModelPepAsociadoViewModelCumplimientoViewModelFatcaViewModelContactosViewModelDireccionViewModelDireccionViewModelEmpresaViewModelDireccionViewModelLaboralViewModelReferenciaViewModelClienteViewModel'> =
      {
        tipoId: model.customer.identification?.type
          ? DocumentTypeEnum[model.customer.identification?.type].code
          : '',
        contactos: {
          correoElectronico: model.contact?.email,
          telCelular: model.contact?.phone,
        },
        direccion: Address.map.toDireccionViewModel(model.address),
      }

    const personIdentifications: Identification['type'][] = ['C', 'P']

    if (
      !model.customer.identification.type ||
      personIdentifications.includes(model.customer?.identification.type)
    ) {
      requestModel.tipoCliente = 'PERSONA'
      requestModel.apellido1 = model.customer?.firstLastName
      requestModel.apellido2 = model.customer?.secondLastName
      requestModel.documentoId = model.customer?.identification.number?.replace(/\D/g, '').trim()
      requestModel.tipoId = model.customer?.identification.type
      //requestModel.fechaNacimiento = model.customer?.birthDate?.toJSON()
      requestModel.nombres = model.customer.name
      requestModel.sexo =
        model.customer.gender === 'Unknown' || !model.customer.gender
          ? undefined
          : GenderEnum[model.customer?.gender].label
      requestModel.nacionalidad = model.customer.identification.nationality?.name
      requestModel.nacionalidadId = model.customer.identification.nationality?.id
    }

    if (model.customer?.identification.type === 'R') {
      requestModel.tipoCliente = 'EMPRESA'

      requestModel.nombres = model.customer.identification.entityName

      requestModel.empresa = {
        nombre: model.customer.identification.entityName,
        personaContacto: Person.getFullName(model.customer),
        telefonoPersonaContacto: model.contact?.phone,
        correoElectronico: model.contact?.email,
        telefono: model.contact?.phone,
      }
    }

    return requestModel
  },
  toFillClienteByAseguradoViewModel(
    model: Contract
  ): SiteApiSchema<'IFillClienteByAseguradoViewModel'> {
    const { RemoteConfigurationStore } = stores()

    const insured = model.insured.customerAsInsured ? model.customer : model.insured

    const requestModel: SiteApiSchema<'IFillClienteByAseguradoViewModel'> = {}
    requestModel.apellido1 = insured.firstLastName
    requestModel.apellido2 = insured.secondLastName
    requestModel.contactos = {
      correoElectronico: model.contact?.email,
      telCelular: model.contact?.phone,
    }

    requestModel.direccion = Address.map.toDireccionViewModel(model.address)
    requestModel.documentoId = insured.identification?.number?.replace(/\D/g, '').trim()
    requestModel.tipoId = insured.identification.type
      ? DocumentTypeEnum[insured.identification.type].label
      : ''
    requestModel.fechaNacimiento = insured.birthDate?.toJSON()
    requestModel.nombres = insured.name
    requestModel.sexo =
      insured.gender && insured.gender !== GenderEnum.Unknown.code
        ? GenderEnum[insured.gender].accronym
        : ''

    if (RemoteConfigurationStore?.value?.form?.requiereAntropometricos == true) {
      requestModel.biometricos = Biometrics.map.BiometricosViewModel(model.biometrics)
    }

    if (
      RemoteConfigurationStore?.value?.form?.requiereCondicionesAsegurabilidad == true &&
      model.specialConditions != null
    ) {
      requestModel.selectedCondicionesAsegurabilidad =
        SpecialConditions.map.toSelectedCondicionesAsegurabilidad(model.specialConditions)
    }

    return requestModel
  },
  toOportunidadCreationModel({
    model,
    insurable,
    stage,
  }: {
    model: Contract
    insurable: Insurable
    stage: SiteApiSchema<'OportunidadEtapa'>
  }): SiteApiSchema<'OportunidadCreationModel'> {
    const { RemoteConfigurationStore, LocalConfigurationStore } = stores()
    const { comments: insurableComment, insurableAsArgentoModel } =
      getInsurableDescription(insurable)
    const utmComment = UTM.getUTMComments(model.utm)
    const comments = [insurableComment, utmComment].filter(Boolean).join(' ')
    return {
      fecha: DateTime.now().toFormat('yyyy-MM-dd'),
      usuarioId: LocalConfigurationStore.value?.argentoInsuranceConfig?.userId,
      usuario: LocalConfigurationStore.value?.argentoInsuranceConfig?.userName,
      brokerId: RemoteConfigurationStore?.value?.broker?.id,
      broker: RemoteConfigurationStore?.value?.broker?.nombre,
      //sucursalId: number | null;
      //sucursal: string | null;
      tipoOportunidad: 'LEAD',
      //fechaCierre: string | null;
      fullName: Person.getFullName(model.customer),
      etapa: stage,
      //codigo: string | null;
      nombres: model.customer.name,
      apellido1: model.customer.firstLastName,
      apellido2: model.customer.secondLastName,
      tipoId: model.customer.identification.type
        ? DocumentTypeEnum[model.customer.identification.type].code
        : undefined,
      identificacion: model.customer.identification.number,
      fechaNacimiento: model.customer?.birthDate
        ? DateTime.fromJSDate(model.customer.birthDate).toISODate()
        : undefined,
      sexo: model.customer.gender ? GenderEnum[model.customer.gender]?.accronym : undefined,
      //nacionalidadId: string | null,
      //nacionalidad: string | null,
      //ocupacionId: string | null,
      //ocupacion: string | null,
      //estadoCivil: string | null,
      //telHogar: string | null,
      numeroContacto: model.contact.phone,
      //telOficina: string | null,
      correoElectronico: model.contact.email,
      //direccionCalle: string | null,
      //direccionNumero: string | null,
      //direccionEdificio: string | null,
      sectorId: model.address.sector?.id,
      sector: model.address.sector?.name,
      municipioId: model.address.municipality?.id,
      municipio: model.address.municipality?.name,
      provinciaId: model.address.province?.id,
      provincia: model.address.province?.name,
      emisorId: RemoteConfigurationStore.value?.emisor?.id,
      //intermediarioId: number | null,
      //intermediarioNombre: string | null,
      productoId:
        model.coverage.argentoInsuranceProductId ?? RemoteConfigurationStore.value?.producto?.id,
      productoNombre: model.coverage.argentoInsuranceProductName,
      //numeroContacto: string | null,
      paisId: model.address.country?.id,
      pais: model.address.country?.name,
      comentario: comments,
      extraDataAsJson: JSON.stringify({
        asegurable: insurableAsArgentoModel,
        plan: model.coverage.planName?.toUpperCase(),
        parametrosDeTarifa: model.coverage.argentoInsuranceCoverageRequestData,
        utm: Object.values(model.utm).filter(Boolean).length > 0 ? model.utm : undefined,
      }),
      //executedBy: undefined,
    }
  },
  toOportunidadEditionModel({
    model,
    insurable,
    stage,
    id,
  }: {
    model: Contract
    insurable: Insurable
    stage: SiteApiSchema<'OportunidadEtapa'>
    id: number
  }): SiteApiSchema<'OportunidadEditionModel'> {
    const result = map.toOportunidadCreationModel({ model, insurable, stage })
    return { id, ...result }
  },
}

export const Contract = {
  load,
  map,
}
